import { Toast } from '@zep-us/zus/toast';
import { type TFunction } from 'i18next';
import '../consts/events.quiz-folder';
import type { EventMap } from '@zep/logger';
import { trackEvent } from '@zep/logger/tracking';
import { zusToast, type ZusToastId, type ZusToastPosition } from '@zep/toast';

/** 진행 토스트를 띄우기 전에 기다리는 시간. 대부분의 저장은 이보다 빨리 끝나므로 오래 걸릴 때만 알린다. */
const LOADING_DELAY_MS = 600;

/** 결과 토스트가 머무는 시간. */
const RESULT_DURATION_MS = 4_000;

/**
 * `보러 가기` 버튼이 붙은 결과 토스트가 머무는 시간. 폴더 화면·공유 창 복사는 제자리에 남아
 * 이 버튼이 사본으로 가는 유일한 길이라, 4초면 놓치기 쉽다 (10/22 QA, 지민).
 */
const ACTION_RESULT_DURATION_MS = 8_000;

/**
 * 진행·결과 토스트 위치. 진행 → 결과가 같은 id 로 교체되므로 모두 같은 값을 준다.
 * 폴더 저장·복사·빌드 복제·클래스 연결 창 결과 토스트가 같이 쓴다.
 * 피그마 클래스 연결 유도(TASK-2206)의 토스트는 모두 아래 가운데라 복사·저장 결과도 맞춘다 (10/21 지민).
 */
export const QUIZ_SAVE_TOAST_POSITION = 'bottom-center' as const;
export interface QuizSaveSuccessFeedback {
  title: string;
  /** 있을 때만 `보러 가기` 가 붙는다. 갈 곳이 없으면 버튼도 없다. */
  href?: string;
  actionLabel?: string;
  /** 목적지로 보낸다. 라우터를 이 모듈이 알지 않도록 호출부가 넘긴다. */
  onNavigate?: (href: string) => void;
  /** 기본은 `QUIZ_SAVE_TOAST_POSITION`. 도착 화면에 아래 시트가 뜰 때만 호출부가 바꾼다. */
  position?: ZusToastPosition;
  /** `보러 가기` 를 누르면 남길 계측. 어디서 시작한 저장인지는 호출부만 안다. */
  navigateEvent?: EventMap['quiz_folder_save_result_navigate'];
}
export interface QuizSaveFailureFeedback {
  title: string;
  /**
   * 있을 때만 `다시 시도` 가 붙는다.
   *
   * ⚠️ 멱등이 보장된 요청에만 넘긴다 — 복사는 다시 부르면 복사본이 둘 생길 수 있다.
   */
  onRetry?: () => void;
}
export interface QuizSaveFeedbackSession {
  success: (feedback: QuizSaveSuccessFeedback) => void;
  failure: (feedback: QuizSaveFailureFeedback) => void;
  /** 결과 대신 알럿·확인 다이얼로그로 흐름이 넘어갈 때. 진행 토스트만 걷는다. */
  dismiss: () => void;
}

/** 진행 상태가 없는 이동 경로에서도 동일한 ZUS 결과 토스트를 쓴다. */
export const showQuizSaveSuccess = ({
  title,
  href,
  actionLabel,
  onNavigate,
  position = QUIZ_SAVE_TOAST_POSITION,
  navigateEvent
}: QuizSaveSuccessFeedback, replaceId?: ZusToastId) => {
  const canNavigate = !!href && !!onNavigate && !!actionLabel;
  // 성공 토스트는 ZUS 기본 체크 아이콘(CheckSmallCircleFill)을 그대로 쓴다.
  const resultId = zusToast.success(title, {
    id: replaceId,
    duration: canNavigate ? ACTION_RESULT_DURATION_MS : RESULT_DURATION_MS,
    position,
    action: canNavigate ? {
      label: actionLabel,
      onClick: () => {
        if (navigateEvent) {
          trackEvent('quiz_folder_save_result_navigate', navigateEvent);
        }
        zusToast.dismiss(resultId);
        onNavigate(href);
      }
    } : undefined
  });
};

/**
 * 폴더 저장·이동의 진행/완료/실패 안내를 한 묶음으로 다룬다.
 *
 * 표현만 갖는다. 어떤 요청을 보냈는지, 재시도 가능한지, 어디로 보낼지는 호출부가 정해서 넘긴다.
 *
 * 진행 토스트가 이미 떴다면 같은 id 로 교체한다.
 * 실패는 `zusToast.error`(destructive) 대신 `custom` 안에서 ZUS `Toast` 를 `default` 로 그린다.
 */
export const beginQuizSaveFeedback = (t: TFunction<['common']>): QuizSaveFeedbackSession => {
  let loadingToastId: ZusToastId | undefined;
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) return;
    loadingToastId = zusToast.loading(t('common:folderSave.loading'), {
      position: QUIZ_SAVE_TOAST_POSITION
    });
  }, LOADING_DELAY_MS);

  /** 진행 토스트 예약을 끊는다. 결과가 정해졌거나 흐름이 다른 데로 넘어갈 때 부른다. */
  const settle = () => {
    settled = true;
    clearTimeout(timer);
  };
  return {
    success: feedback => {
      settle();
      showQuizSaveSuccess({
        ...feedback,
        actionLabel: feedback.actionLabel ?? t('common:folderSave.viewFolder')
      }, loadingToastId);
    },
    failure: ({
      title,
      onRetry
    }) => {
      settle();
      zusToast.custom(({
        dismiss
      }) => <Toast variant={'default'} title={title} onClose={dismiss} action={onRetry ? {
        label: t('common:folderSave.retry'),
        onClick: () => {
          dismiss();
          onRetry();
        }
      } : undefined} />, {
        id: loadingToastId,
        duration: RESULT_DURATION_MS,
        position: QUIZ_SAVE_TOAST_POSITION
      });
    },
    dismiss: () => {
      settle();
      if (loadingToastId !== undefined) zusToast.dismiss(loadingToastId);
    }
  };
};